import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { adminListLlbTutoring } from "@/lib/api/llb-tutoring.functions";
import { CheckCircle2, AlertCircle, GraduationCap, RefreshCw } from "lucide-react";

export const Route = createFileRoute("/_authenticated/portal/admin/llb-tutoring")({
  component: AdminLlbTutoring,
});

function fmt(n: any) { return `R${Number(n ?? 0).toFixed(2)}`; }
function ts(s: string | null) { return s ? new Date(s).toLocaleString("en-ZA") : "—"; }

function AdminLlbTutoring() {
  const load = useServerFn(adminListLlbTutoring);
  const q = useQuery({ queryKey: ["admin-llb-tutoring"], queryFn: () => load() });
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<"all" | "paid" | "pending" | "other">("all");
  const [enrolment, setEnrolment] = useState<"all" | "full" | "partial">("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const rows = (q.data?.rows ?? []) as any[];
  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    const fromTs = from ? new Date(from).getTime() : null;
    const toTs = to ? new Date(to).getTime() + 86400000 : null;
    return rows.filter((r) => {
      if (s) {
        const hit =
          r.reference?.toLowerCase().includes(s) ||
          r.student?.email?.toLowerCase().includes(s) ||
          r.student?.full_name?.toLowerCase().includes(s) ||
          r.student?.student_number?.toLowerCase().includes(s) ||
          (r.module_codes ?? []).some((c: string) => c.toLowerCase().includes(s));
        if (!hit) return false;
      }
      if (status !== "all") {
        if (status === "paid" && r.status !== "paid") return false;
        if (status === "pending" && r.status !== "pending") return false;
        if (status === "other" && (r.status === "paid" || r.status === "pending")) return false;
      }
      if (enrolment !== "all") {
        const expected = (r.module_codes ?? []).length;
        const enrolled = (r.enrolments ?? []).filter((e: any) => e.status === "enrolled").length;
        const full = expected > 0 && enrolled >= expected;
        if (enrolment === "full" && !full) return false;
        if (enrolment === "partial" && full) return false;
      }
      const created = r.created_at ? new Date(r.created_at).getTime() : 0;
      if (fromTs && created < fromTs) return false;
      if (toTs && created >= toTs) return false;
      return true;
    });
  }, [rows, search, status, enrolment, from, to]);

  const totalPaid = filtered.filter((r) => r.status === "paid").reduce((s, r) => s + Number(r.amount_zar ?? 0), 0);
  const totalDiscount = filtered.reduce((s, r) => s + Number(r.discount_zar ?? 0), 0);
  const totalPending = filtered.filter((r) => r.status !== "paid").length;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl text-navy flex items-center gap-2"><GraduationCap className="h-5 w-5"/>LLB tutoring — admin</h1>
          <p className="text-sm text-muted-foreground">Module selections, PayFast statuses, discount calculations and auto-enrolment outcomes per student.</p>
        </div>
        <Button size="sm" variant="outline" onClick={() => q.refetch()}><RefreshCw className="h-4 w-4 mr-1"/>Refresh</Button>
      </header>

      <section className="grid gap-3 sm:grid-cols-4">
        <Stat label="Orders (filtered)" value={filtered.length} />
        <Stat label="Paid total" value={fmt(totalPaid)} />
        <Stat label="Discount given" value={fmt(totalDiscount)} />
        <Stat label="Pending orders" value={totalPending} />
      </section>

      <div className="rounded-xl border border-border bg-card p-3 grid gap-2 md:grid-cols-5">
        <Input className="md:col-span-2" placeholder="Search reference / student / module…" value={search} onChange={(e) => setSearch(e.target.value)} />
        <select
          className="rounded-md border border-input bg-background px-3 py-2 text-sm"
          value={status}
          onChange={(e) => setStatus(e.target.value as any)}
        >
          <option value="all">All payment statuses</option>
          <option value="paid">Paid</option>
          <option value="pending">Pending</option>
          <option value="other">Other</option>
        </select>
        <select
          className="rounded-md border border-input bg-background px-3 py-2 text-sm"
          value={enrolment}
          onChange={(e) => setEnrolment(e.target.value as any)}
        >
          <option value="all">Any enrolment</option>
          <option value="full">Fully auto-enrolled</option>
          <option value="partial">Not fully enrolled</option>
        </select>
        <div className="flex items-center gap-1 md:col-span-5">
          <span className="text-xs text-muted-foreground">Created between</span>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-auto" />
          <span className="text-xs text-muted-foreground">and</span>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-auto" />
          {(from || to || status !== "all" || enrolment !== "all" || search) && (
            <Button size="sm" variant="ghost" onClick={() => { setSearch(""); setStatus("all"); setEnrolment("all"); setFrom(""); setTo(""); }}>
              Reset
            </Button>
          )}
        </div>
      </div>


      {q.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-muted-foreground">No orders match.</p>
      ) : (
        <div className="space-y-4">
          {filtered.map((r) => {
            const latestEvent = r.events?.[0];
            const enrolledCount = (r.enrolments ?? []).filter((e: any) => e.status === "enrolled").length;
            const expected = (r.module_codes ?? []).length;
            const fullyEnrolled = enrolledCount >= expected && expected > 0;
            return (
              <article key={r.id} className="rounded-xl border border-border bg-card p-4 text-sm">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-mono text-xs text-muted-foreground">{r.reference}</p>
                    <p className="font-semibold text-navy">
                      {r.student?.full_name || r.student?.email || "Unknown student"}
                      {r.student?.student_number ? <span className="ml-2 font-mono text-xs text-muted-foreground">{r.student.student_number}</span> : null}
                    </p>
                    <p className="text-xs text-muted-foreground">{r.student?.email}</p>
                  </div>
                  <div className="text-right text-xs">
                    <p>Created {ts(r.created_at)}</p>
                    {r.status === "paid"
                      ? <p className="text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3"/>Paid · {ts(r.paid_at)}</p>
                      : <p className="text-amber-700 inline-flex items-center gap-1"><AlertCircle className="h-3 w-3"/>{r.status}</p>}
                  </div>
                </div>

                <div className="mt-3 grid gap-3 md:grid-cols-3">
                  <div className="rounded-md bg-muted/40 p-3">
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Modules ({r.module_count}) · {r.semester}</p>
                    <p className="mt-1 font-mono text-xs">{(r.module_codes ?? []).join(", ")}</p>
                  </div>
                  <div className="rounded-md bg-muted/40 p-3">
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Pricing</p>
                    <p className="mt-1 text-xs">Subtotal {fmt(r.subtotal_zar)}</p>
                    <p className="text-xs text-emerald-700">Discount −{fmt(r.discount_zar)}</p>
                    <p className="mt-1 text-sm font-semibold">Total {fmt(r.amount_zar)}</p>
                  </div>
                  <div className="rounded-md bg-muted/40 p-3">
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Auto-enrolment</p>
                    <p className={`mt-1 text-xs ${fullyEnrolled ? "text-emerald-700" : "text-amber-700"}`}>
                      {enrolledCount}/{expected} enrolled
                    </p>
                    <p className="mt-1 text-[11px] text-muted-foreground">Latest event: {latestEvent?.event_type ?? "—"}</p>
                    {latestEvent?.error ? <p className="text-rose-700 text-[11px]">{latestEvent.error}</p> : null}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: any }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums text-navy">{value}</p>
    </div>
  );
}
